import { useState, useRef, useCallback } from "react";

/**
 * Fetches /api/customer/profile and caches the result for the lifetime of
 * the component. Call `load()` to trigger the fetch — safe to call multiple
 * times (subsequent calls are ignored while loading or after a successful
 * load). Pairs with an `onError` callback so callers keep their own error
 * messaging style.
 */
export function useCustomerProfile({ api, onError }) {
  const [customers, setCustomers] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    if (isLoading || customers !== null) return;
    setIsLoading(true);
    try {
      const response = await api.get("/api/customer/profile", {
        suppressGlobalError: true,
      });
      if (!isMounted.current) return;
      if (response.status === 200) {
        setCustomers(response.data ?? []);
      }
    } catch {
      if (isMounted.current) onError?.();
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [api, customers, isLoading, onError]);

  return { customers, isLoading, load };
}
